import type { ScenarioAttributeNode } from '../../../features/scenarios/api';
import type { HierarchicalOption } from '../../common/HierarchicalSelect';

// シナリオ全体の「モーショングラフィックス」(属性ツリー scenario_basic > motion_graphics > option・
// docs/features/brand-cut-motion-graphics-plan.md §10.14)。撮影方法・方言と同じ独立フィールドの3択:
//   ''       = 指定なし (ノードなし・既定)。シーン単位の提案は AI に押さない
//   'scenes' = シーンに混ぜる。提案を積極的に求める
//   'whole'  = 全編モーション (制作エンジン 'motion'・BGM先行モーダル等)
// option key = payload の値。ツリーに field が無い環境 (migration 未適用) では選択肢が空になり、新項目を出さない。
export const MOTION_GRAPHICS_FIELD_KEY = 'motion_graphics';
export const MOTION_GRAPHICS_SCENES = 'scenes';
export const MOTION_GRAPHICS_WHOLE = 'whole';
export type MotionGraphicsValue = '' | typeof MOTION_GRAPHICS_SCENES | typeof MOTION_GRAPHICS_WHOLE;
// 移行前の映像スタイル option の key。localStorage / 保存済みシナリオに残っていたら whole へ読み替える
export const LEGACY_MOTION_VISUAL_STYLE = 'motion_graphics';

export function normalizeMotionGraphics(raw: unknown): MotionGraphicsValue {
  const key = typeof raw === 'string' ? raw.trim() : '';
  return key === MOTION_GRAPHICS_SCENES || key === MOTION_GRAPHICS_WHOLE ? key : '';
}

/** field 直下の option を HierarchicalOption に変換する (metadata 付き)。field が無ければ空配列。 */
export function deriveMotionGraphicsOptions(items?: ScenarioAttributeNode[]) {
  const find = (nodes: ScenarioAttributeNode[]): ScenarioAttributeNode | undefined => {
    for (const node of nodes) {
      if (node.key === MOTION_GRAPHICS_FIELD_KEY && node.node_type === 'group') return node;
      const found = find(node.children ?? []);
      if (found) return found;
    }
  };
  const field = find(items ?? []);
  const motionGraphicsOptions: HierarchicalOption[] = (field?.children ?? [])
    .filter((node) => node.node_type === 'option' && normalizeMotionGraphics(node.key) !== '')
    .map((node) => ({ id: node.id, key: node.key, label: node.label, type: 'option', metadata: node.metadata ?? null }));
  return {
    motionGraphicsOptions,
    motionGraphicsFieldPlacement: (field?.placement === 'advanced' ? 'advanced' : 'primary') as 'primary' | 'advanced',
  };
}

/** ツリーで選べる値だけを有効値にする (field 未作成・パートナーの除外で見えない値は指定なし)。 */
export function effectiveMotionGraphics(value: string, options: HierarchicalOption[]): MotionGraphicsValue {
  const key = normalizeMotionGraphics(value);
  return key && options.some((o) => o.key === key) ? key : '';
}

/** 正式名 (ツールチップ用。metadata.full_label が無ければ label)。 */
export function motionGraphicsFullLabel(option: HierarchicalOption): string {
  const full = option.metadata?.full_label;
  return typeof full === 'string' && full.trim() ? full.trim() : option.label;
}

/**
 * 移行後 (field がある) に、旧映像スタイル `motion_graphics` を新項目 whole + 映像スタイル既定へ読み替える。
 * 読み替えが不要なら null。移行前 (field が無い) は旧 option がまだ映像スタイルにあるので読み替えない。
 */
export function migrateLegacyMotionVisualStyle(
  visualStyle: string,
  options: HierarchicalOption[],
): { visualStyle: string; motionGraphics: MotionGraphicsValue } | null {
  if (visualStyle !== LEGACY_MOTION_VISUAL_STYLE) return null;
  if (!options.some((o) => o.key === MOTION_GRAPHICS_WHOLE)) return null;
  return { visualStyle: 'photorealistic', motionGraphics: MOTION_GRAPHICS_WHOLE };
}
